import React, { useState, useEffect, forwardRef } from "react";
import { useHistory } from "react-router-dom";

import {
  Button,
  Dialog,
  Typography,
  Slide,
  Grid,
  CircularProgress,
  Box,
  TextField,
  MenuItem,
} from "@mui/material";

import { apiGet, API } from "@services/API";

import { useSnackbar } from "notistack";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function headers() {
  let headers = {};
  let token = localStorage.getItem("covid19_token");
  let mfa_token = localStorage.getItem("mfa_token");
  if (token && token.length > 0 && mfa_token && mfa_token.length > 0) {
    headers = { "x-access-token": token };
  }
  return headers;
}

export default function HospitalAddEditDialog({
  isVisible,
  onClose,
  onSave,
  hospitalID,
  ...props
}) {
  let history = useHistory();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);

  const [hospitalName, setHospitalName] = useState("");
  const [hospitalLocation, setHospitalLocation] = useState("");
  const [hospitalDataController, setHospitalDataController] = useState("");

  const [dataHospitalDataControllers, setDataHospitalDataControllers] =
    useState([]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      if (hospitalID !== null) {
        apiGet(`/hospital/${hospitalID}`, { headers: headers() }, history)
          .then((response) => {
            const hospitalDetails = response.data[0];

            setHospitalName(hospitalDetails.name);
            setHospitalLocation(hospitalDetails.location);
            setHospitalDataController(hospitalDetails.data_controller_id);

            apiGet("/users", { headers: headers() }, history)
              .then((response) => {
                setDataHospitalDataControllers(response.data.users);

                setLoading(false);
              })
              .catch((error) => {
                setDataHospitalDataControllers([]);
                setLoading(false);
              });
          })
          .catch((error) => {
            setLoading(false);
            setHospitalName("");
            setHospitalLocation("");
            setDataHospitalDataControllers([]);
            setHospitalDataController("");
          });
      } else {
        apiGet("/users", { headers: headers() }, history)
          .then((response) => {
            setDataHospitalDataControllers(response.data.users);

            setLoading(false);
          })
          .catch((error) => {
            setDataHospitalDataControllers([]);
            setLoading(false);
          });
      }
    }, 200);

    return () => clearTimeout(timeOut);
  }, []);

  const showSnackBar = (type, title, subtitle) => {
    const options = { variant: type };

    enqueueSnackbar(
      <div style={{ display: "inline-grid" }}>
        <Typography data-test={"snack-title"} variant="h6">
          {title}
        </Typography>
        <Typography data-test={"snack-text"} variant="subtitle1">
          {subtitle}
        </Typography>
      </div>,
      options
    );
  };

  const isNullOrEmpty = (value) => {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === "string" && value.trim().toLowerCase() === "null") {
      return true;
    }

    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    if (typeof value === "string" && value.trim() === "") {
      return true;
    }

    if (typeof value === "number" && isNaN(value)) {
      return true;
    }

    return false;
  };

  const handleSave = () => {
    if (isNullOrEmpty(hospitalName)) {
      showSnackBar("error", "Failure", "Hospital name can't be empty.");
    } else if (isNullOrEmpty(hospitalLocation)) {
      showSnackBar("error", "Failure", "Hospital location can't be empty.");
    } else if (isNullOrEmpty(hospitalDataController)) {
      showSnackBar(
        "error",
        "Failure",
        "Hospital data controller can't be empty."
      );
    } else {
      const hospitalBody = {
        id: hospitalID,
        name: hospitalName,
        location: hospitalLocation,
        data_controller_id: hospitalDataController,
      };

      API.post("/hospital", hospitalBody, {
        headers: headers(),
      })
        .then((response) => {
          showSnackBar(
            "success",
            "Success",
            "Successfully saved hospital details."
          );

          onSave();
        })
        .catch((error) => {
          showSnackBar("error", "Failure", "Failed to save hospital details.");
        });
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={isVisible}
      onClose={handleClose}
      TransitionComponent={Transition}
      PaperProps={{
        style: {
          backgroundColor: "#FFF",
          boxShadow: "none",
        },
      }}
    >
      <Box
        style={{
          display: "flex",
          justifyContent: "center",
          margin: 15,
        }}
      >
        {loading ? (
          <CircularProgress color="primary" />
        ) : (
          <>
            <Grid
              container
              spacing={2}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Grid
                item
                xs={12}
                md={12}
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography data-test={"hospital-add-edit-title"} variant="h6">
                  Hospital Details
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  data-test={"hospital-add-edit-name"}
                  label="Name"
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                  value={hospitalName}
                  onChange={(e) => setHospitalName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  data-test={"hospital-add-edit-location"}
                  label="Location"
                  variant="outlined"
                  size="small"
                  required
                  fullWidth
                  value={hospitalLocation}
                  onChange={(e) => setHospitalLocation(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <TextField
                  data-test={"hospital-add-edit-data-controller"}
                  id="outlined-select-data-controller"
                  select
                  size="small"
                  required
                  fullWidth
                  label="Data Controller for the hospital access"
                  value={hospitalDataController}
                  onChange={(e) => setHospitalDataController(e.target.value)}
                  SelectProps={{
                    multiple: false,
                  }}
                >
                  {dataHospitalDataControllers !== null &&
                    dataHospitalDataControllers.length > 0 &&
                    dataHospitalDataControllers.map((item) => (
                      <MenuItem
                        data-test={`hospital-add-edit-data-controller-select-${
                          item.firstname + `-` + item.surname
                        }`}
                        key={item.user_id}
                        value={item.user_id}
                      >
                        {item.firstname + " " + item.surname}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              <Grid
                item
                xs={12}
                md={12}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  flexDirection: "row",
                }}
              >
                <Button
                  data-test={"hospital-add-edit-cancel-btn"}
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: 10 }}
                  onClick={handleClose}
                >
                  Cancel
                </Button>

                {!isNullOrEmpty(hospitalName) &&
                  !isNullOrEmpty(hospitalLocation) &&
                  !isNullOrEmpty(hospitalDataController) && (
                    <Button
                      data-test={"hospital-add-edit-save-btn"}
                      variant="contained"
                      color="primary"
                      style={{ marginLeft: 10 }}
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                  )}
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Dialog>
  );
}
